/* eslint-disable no-script-url */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import nextLocale from '../locale/zh-cn';
import Icon from '../icon';
import Animate from '../animate';
import Button from '../button';
import ConfigProvider from '../config-provider';

const noop = () => {};
const shapeToIcon = {
  success: 'tick-o-fill',
  warning: 'warning-o-fill',
  notice: 'info-o-fill',
  loading: 'loading',
  error: 'remove-o-fill',
};

/**
 * Message
 */
class Message extends Component {
  static displayName = 'Message';

  static propTypes = {
    prefix: PropTypes.string,
    pure: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    /**
     * 反馈类型
     */
    type: PropTypes.oneOf(['success', 'warning', 'error', 'notice', 'help', 'loading']),
    /**
     * 反馈外观
     */
    shape: PropTypes.oneOf(['inline', 'addon', 'toast', 'global']),
    /**
     * 反馈大小
     */
    size: PropTypes.oneOf(['medium', 'large']),
    /**
     * 标题
     */
    title: PropTypes.node,
    /**
     * 内容
     */
    children: PropTypes.node,
    /**
     * 默认是否显示
     */
    defaultVisible: PropTypes.bool,
    /**
     * 当前是否显示
     */
    visible: PropTypes.bool,
    /**
     * 显示的图标类型，会覆盖内部设置的IconType
     */
    iconType: PropTypes.string,
    /**
     * 操作行动点，文字或者 JSX.Element
     */
    handler: PropTypes.string,
    /**
     * 显示关闭按钮
     */
    closeable: PropTypes.bool,
    /**
     * 关闭按钮的回调
     */
    onClose: PropTypes.func,
    /**
     * 关闭之后调用的函数
     */
    afterClose: PropTypes.func,
    /**
     * 点击行动点的回掉
     */
    onClickHandler: PropTypes.func,
    /**
     * 是否开启展开收起动画
     */
    animation: PropTypes.bool,
    locale: PropTypes.object,
    rtl: PropTypes.bool,
  };

  static defaultProps = {
    prefix: 'next-',
    pure: false,
    type: 'success',
    shape: 'inline',
    size: 'medium',
    defaultVisible: true,
    closeable: false,
    onClose: noop,
    afterClose: noop,
    animation: true,
    locale: nextLocale.Message,
  };

  state = {
    visible:
      typeof this.props.visible === 'undefined' ? this.props.defaultVisible : this.props.visible,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ('visible' in nextProps) {
      this.setState({
        visible: nextProps.visible,
      });
    }
  }

  onClose = () => {
    if (!('visible' in this.props)) {
      this.setState({
        visible: false,
      });
    }
    this.props.onClose(false);
  };

  globalButton = (messagePrefix) => {
    const { type, onClickHandler, handler } = this.props;
    const cls = `${messagePrefix}-handler`;

    switch (type) {
      case 'error':
        return <Button className={cls} type="normal" size="xs" warning onClick={onClickHandler}>{handler}</Button>;
      case 'help':
        return <Button className={cls} type="primary" size="xs" onClick={onClickHandler}>{handler}</Button>;
      default:
        return <Button className={cls} type="normal" size="xs" onClick={onClickHandler}>{handler}</Button>;
    }
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      prefix,
      pure,
      className,
      type,
      shape,
      size,
      title,
      children,
      defaultVisible,
      visible: propsVisible,
      iconType: icon,
      handler,
      closeable,
      onClose,
      afterClose,
      onClickHandler,
      animation,
      rtl,
      locale,
      ...others
    } = this.props;
    /* eslint-enable */
    const { visible } = this.state;
    const messagePrefix = `${prefix}message`;

    const classes = classNames({
      [messagePrefix]: true,
      [`${prefix}message-${type}`]: type,
      [`${prefix}${shape}`]: shape,
      [`${prefix}${size}`]: size && shape !== 'global',
      [`${prefix}title-content`]: !!title,
      [`${prefix}only-content`]: !title && !!children && shape !== 'global',
      [className]: className,
    });

    const content = shape === 'global'
      ? (
        <div className={`${messagePrefix}-content-wrapper`}>
          {children ? <div className={`${messagePrefix}-content`}>{children}</div> : null}
          {handler ? this.globalButton(messagePrefix) : null }
        </div>
      )
      : (
        <div className={`${messagePrefix}-content-wrapper`}>
          <div className={`${messagePrefix}-title-wrapper`}>
            {title ? <div className={`${messagePrefix}-title`}>{title}</div> : null}
            {handler ? <Button className={`${messagePrefix}-handler`} text type="primary" onClick={onClickHandler}>{handler}</Button> : null }
          </div>
          {children ? <div className={`${messagePrefix}-content`}>{children}</div> : null}
        </div>
      );

    const newChildren = visible ? (
      <div role="alert" {...others} className={classes} dir={rtl ? 'rtl' : undefined}>
        {shape !== 'global' ? <Icon className={`${messagePrefix}-symbol ${!icon && `${messagePrefix}-symbol-icon`}`} type={icon || shapeToIcon[type] || 'info-o-fill'} /> : null}
        {content}
        {closeable ? (
          <a
            role="button"
            aria-label={locale.closeAriaLabel}
            href="javascript:;"
            className={`${messagePrefix}-close`}
            onClick={this.onClose}
          >
            <Icon type="remove" />
          </a>
        ) : null}
      </div>
    ) : null;

    if (animation) {
      return (
        <Animate.Expand animationAppear={false} afterLeave={afterClose}>
          {newChildren}
        </Animate.Expand>
      );
    }

    return newChildren;
  }
}

export default ConfigProvider.config(Message);
